import { restRequest, post, get } from './restClient';
import { API_ENDPOINTS } from './config';

interface ChatResponse {
  response: string;
  sources?: any[];
  followup_questions?: any[];
  session_id: string;
  request_id?: string;
  usage_metrics?: any;
}

interface UserContext {
  user_id: string;
  user_role: string;
  department: string;
  email?: string | null;
  given_name?: string | null;
  family_name?: string | null;
}

export const sendChatMessage = async (
  message: string,
  sessionId: string,
  userContext: UserContext
): Promise<ChatResponse> => {
  // Validate parameters
  if (!message || typeof message !== 'string' || message.trim() === '') {
    throw new Error('Message cannot be empty');
  }

  if (!sessionId || typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new Error('Session ID is required');
  }

  if (
    !userContext ||
    !userContext.user_id ||
    userContext.user_id.trim() === ''
  ) {
    throw new Error('User context with user_id is required');
  }

  // Normalize user context with defaults
  const normalizedUserContext = {
    user_id: userContext.user_id,
    user_role: userContext.user_role || 'DUA Staff',
    department: userContext.department || 'unknown',
    email: userContext.email || null,
    given_name: userContext.given_name || null,
    family_name: userContext.family_name || null,
  };

  // Prepare REST API request body
  const requestBody = {
    message: message.trim(),
    session_id: sessionId,
    user_context: normalizedUserContext,
  };

  try {
    const result = await post<any>(
      API_ENDPOINTS.CHAT_MESSAGE || API_ENDPOINTS.CHAT,
      requestBody,
      {
        maxRetries: 2,
        baseDelay: 2000,
        maxDelay: 8000,
      }
    );

    // Handle the REST response
    if (!result) {
      throw new Error('Invalid response format from server');
    }

    // Handle wrapped response from /api/chat/message endpoint
    let actualResponse = result;
    if (result.success && result.data) {
      actualResponse = result.data;
    }

    // Extract and format the response - handle both 'content' and 'response' field names
    const responseText = actualResponse.content || actualResponse.response || '';
    
    // Format follow-up questions
    const followupQuestions = (actualResponse.followUpQuestions || actualResponse.followup_questions || []).map((question: any) => ({
      question: typeof question === 'string' ? question : question.question,
      relevance_score: question.relevance_score || 0,
      category: question.category || 'general',
      is_dua_relevant: question.is_dua_relevant || false,
    }));

    return {
      response: responseText,
      sources: actualResponse.sources || [],
      followup_questions: followupQuestions,
      session_id: actualResponse.sessionId || actualResponse.session_id || sessionId,
      request_id: actualResponse.id || actualResponse.request_id,
      usage_metrics: actualResponse.metadata || actualResponse.usage_metrics || {},
    };
  } catch (error: any) {
    // Handle specific error types
    if (error && typeof error === 'object') {
      if (error.isTimeout === true) {
        throw new Error(
          'Request is taking longer than expected. Please try again.'
        );
      }

      if (error.isNetworkError === true) {
        throw new Error(
          'Unable to connect to the server. Please check your internet connection.'
        );
      }

      if (error.status === 400) {
        // Extract error message from backend response format: { error: { message: "...", code: "..." } }
        const errorMessage = error.responseBody?.error?.message ||
                           error.responseBody?.message ||
                           error.message ||
                           'Invalid request';
        throw new Error(errorMessage);
      }

      if (error.status === 429 || error.isRateLimit) {
        throw new Error(
          'Too many requests. Please wait a moment before trying again.'
        );
      }

      if (error.status === 500 || error.isServerError) {
        throw new Error('Server error occurred. Please try again later.');
      }
    }

    // Re-throw the original error if it doesn't match our specific cases
    throw error;
  }
};

export const getSessionInfo = async (sessionId: string) => {
  // Validate session ID parameter
  if (!sessionId || typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new Error('Session ID is required');
  }

  try {
    const result = await get<any>(API_ENDPOINTS.CHAT_SESSION_BY_ID(sessionId));

    if (!result) {
      return {
        session_id: sessionId,
        exists: false,
        analytics: {
          total_conversations: 0,
          active_conversations: 0,
        },
      };
    }

    return {
      session_id: result.session_id || sessionId,
      exists: true,
      title: result.title,
      status: result.status,
      message_count: result.message_count || 0,
      last_message_at: result.last_message_at,
      created_at: result.created_at,
      updated_at: result.updated_at,
      analytics: {
        total_conversations: result.message_count || 0,
        active_conversations: result.status === 'ACTIVE' ? 1 : 0,
      },
    };
  } catch {
    return {
      session_id: sessionId,
      exists: false,
      analytics: {
        total_conversations: 0,
        active_conversations: 0,
      },
    };
  }
};

export const endChatSession = async (sessionId: string) => {
  // Validate session ID parameter
  if (!sessionId || typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new Error('Session ID is required');
  }

  try {
    // Update session status to COMPLETED using REST API
    const result = await post<any>(
      `${API_ENDPOINTS.CHAT_SESSION}/${sessionId}/end`,
      { status: 'COMPLETED' }
    );

    return {
      message: 'Session ended successfully',
      session_id: result?.session_id || sessionId,
    };
  } catch {
    return { message: 'Session ended successfully', session_id: sessionId };
  }
};

export const generateSessionId = (): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `session_${timestamp}_${random}`;
};

export const createChatSession = async (title: string = 'New Chat Session'): Promise<string> => {
  try {
    const result = await post<any>(
      API_ENDPOINTS.CHAT_SESSION,
      {
        title: title,
        metadata: {
          created_at: new Date().toISOString(),
          source: 'web_app',
        },
      }
    );

    return result?.session_id || generateSessionId();
  } catch {
    // Fallback to client-generated ID if REST API fails
    return generateSessionId();
  }
};

export const getChatSessions = async (userId: string, limit: number = 10): Promise<any[]> => {
  try {
    const result = await get<any>(
      `${API_ENDPOINTS.CHAT_SESSIONS}?user_id=${userId}&limit=${limit}`
    );

    return result?.sessions || [];
  } catch {
    return [];
  }
};

export const createUserContext = (user: any): UserContext => {
  if (!user) {
    return {
      user_id: 'anonymous',
      user_role: 'DUA Staff',
      department: 'unknown',
      email: null,
      given_name: null,
      family_name: null,
    };
  }

  return {
    user_id: user.id || user.user_id || 'anonymous',
    user_role: user.role || user.user_role || 'DUA Staff',
    department: user.department || 'unknown',
    email: user.email || null,
    given_name: user.given_name || null,
    family_name: user.family_name || null,
  };
};

export const formatChatMessage = (response: ChatResponse) => {
  return {
    id: response.request_id || 'mock-request-id',
    text: response.response || '',
    sender: 'bot' as const,
    timestamp: new Date(),
    sources: response.sources || [],
    usage_metrics: response.usage_metrics || {},
  };
};

export const formatFollowupQuestions = (questions: any[]) => {
  if (!Array.isArray(questions)) {
    return [];
  }

  return questions.map((q, index) => ({
    id: `followup_${Date.now()}_${index}`,
    question: typeof q === 'string' ? q : (q.question || ''),
    relevance_score: q.relevance_score || 0,
    category: q.category || 'general',
    is_dua_relevant: q.is_dua_relevant || false,
  }));
};

export const validateChatMessage = (message: any) => {
  if (typeof message !== 'string') {
    return { isValid: false, error: 'Message must be a non-empty string' };
  }

  if (message === '') {
    return { isValid: false, error: 'Message must be a non-empty string' };
  }

  if (!message.trim()) {
    return { isValid: false, error: 'Message cannot be empty' };
  }

  if (message.length > 4000) {
    return { isValid: false, error: 'Message too long (max 4000 characters)' };
  }

  // Check for potentially harmful content
  const harmfulPatterns = [
    /<script/i,
    /javascript:/i,
    /onclick=/i,
    /onload=/i,
    /onerror=/i,
  ];

  if (harmfulPatterns.some(pattern => pattern.test(message))) {
    return {
      isValid: false,
      error: 'Message contains potentially harmful content',
    };
  }

  return { isValid: true };
};

export const estimateResponseTime = (message: any): number => {
  if (typeof message !== 'string' || !message.trim()) {
    return 2000; // Base time
  }

  const baseTime = 2000;
  const wordCount = message.split(/\s+/).length;
  const questionCount = (message.match(/\?/g) || []).length;

  // Technical terms that might increase processing time
  const technicalTerms = [
    'unemployment',
    'benefits',
    'eligibility',
    'claim',
    'determination',
  ];
  const technicalTermCount = technicalTerms.reduce((count, term) => {
    return count + (message.toLowerCase().split(term).length - 1);
  }, 0);

  let estimatedTime = baseTime;
  estimatedTime += wordCount * 10; // 10ms per word
  estimatedTime += questionCount * 500; // 500ms per question
  estimatedTime += technicalTermCount * 200; // 200ms per technical term

  // Cap at 4x base time
  return Math.min(estimatedTime, baseTime * 4);
};

export const getChatHealth = async () => {
  try {
    // Use REST API health endpoint
    const result = await get<any>(API_ENDPOINTS.HEALTH);
    
    return {
      status: result?.status === 'healthy' ? 'healthy' as const : 'unhealthy' as const,
      api: true,
      bedrock: result?.bedrock || false,
      cognito: result?.cognito || false,
    };
  } catch (error: any) {
    return {
      status: 'unhealthy' as const,
      error: error.message || 'Health check failed',
    };
  }
};

export default {
  sendChatMessage,
  getSessionInfo,
  endChatSession,
  generateSessionId,
  createChatSession,
  getChatSessions,
  createUserContext,
  formatChatMessage,
  formatFollowupQuestions,
  validateChatMessage,
  estimateResponseTime,
  getChatHealth,
};
